/**
 * Connected cloud projects: which ones a runtime hosts, and which catalog row
 * each of them answers to.
 *
 * Split out of `cloudProjectsGroup.logic.ts` when it went past the file cap.
 * The split is where the seam already was: everything here is about resolving
 * a CONNECTED project against the catalog, while what stays behind is identity
 * and key derivation.
 */
import type { ScopedProjectRef } from "@cmmd-forge/contracts";
import type {
  CloudProjectCatalogRow,
  CmmdForgeRuntimeListSnapshot,
} from "../../cmmd/cmmdForgeRuntimeListResource";
import {
  findNamingCatalogRow,
  findRegisteringCatalogRow,
  isCloudRegistrationLive,
} from "../projects/pickableProject";
import {
  resolveCloudEnvironmentRuntimeId,
  type SidebarCloudRuntimeSources,
} from "./sidebarThreadRuntimeIdentity";
import type { SidebarProjectSnapshot } from "./SidebarProjectItem";
import {
  type CloudProjectIdentity,
  deriveCloudProjectKey,
  deriveCloudRuntimeFallbackKey,
  mergeSidebarProjectMemberRefs,
  resolveCloudProjectEnvironmentId,
  toRegisteringCatalogRow,
} from "./cloudProjectsGroup.logic";

/**
 * How many CONNECTED projects each runtime hosts.
 *
 * Inventory-only rows are listings rather than connections, so they do not make
 * a runtime's identity ambiguous and are not counted.
 */
function countConnectedProjectsByRuntimeId(
  projects: readonly SidebarProjectSnapshot[],
  runtimeSources: SidebarCloudRuntimeSources,
): ReadonlyMap<string, number> {
  const countByRuntimeId = new Map<string, number>();
  for (const project of projects) {
    const runtimeId = resolveCloudEnvironmentRuntimeId(project.environmentId, runtimeSources);
    if (runtimeId === null) continue;
    if (project.source?.actionAuthority === "inventory-only") continue;
    countByRuntimeId.set(runtimeId, (countByRuntimeId.get(runtimeId) ?? 0) + 1);
  }
  return countByRuntimeId;
}

/**
 * The key a connected cloud project should carry.
 *
 * Its own key when the runtime hosts more than one connected project. A runtime
 * is per-owner and hosts every one of that user's cloud projects, so its
 * identity names one of them at most, and handing it to all of them merged them
 * into a single sidebar row (FOR-36359).
 *
 * Otherwise the catalog row's key, which is what carries expansion state across
 * the pre-connect swap: the resolved connected key is repository/scoped-id based
 * and differs, so without this remap a project's expansion state would be lost
 * the instant its VM connects. Cloud rows are isolated here, so this never
 * affects the local/mixed logical-key merge.
 */
function resolveConnectedCloudProjectKey(input: {
  readonly project: SidebarProjectSnapshot;
  readonly runtimeId: string;
  readonly connectedProjectCount: number;
  readonly projectIdentityByRuntimeId: ReadonlyMap<string, CloudProjectIdentity>;
}): string {
  if (input.connectedProjectCount > 1) return input.project.projectKey;
  const identity = input.projectIdentityByRuntimeId.get(input.runtimeId);
  return identity
    ? deriveCloudProjectKey(identity)
    : deriveCloudRuntimeFallbackKey(input.runtimeId);
}

export function splitSidebarProjectsByCloudEnvironment(
  projects: readonly SidebarProjectSnapshot[],
  /**
   * runtimeId → project identity, so a CONNECTED cloud project can be re-keyed
   * onto the same project-scoped key its catalog row uses. Without it a
   * connected project falls back to its runtime-derived key and would split
   * into a second row the moment its VM comes up.
   */
  projectIdentityByRuntimeId: ReadonlyMap<string, CloudProjectIdentity> = new Map(),
  /**
   * Where a durable `env_` Environment's runtime is looked up, since its id
   * encodes none. Omitted, only the legacy locator resolves, which is what
   * every caller outside the sidebar wants.
   */
  runtimeSources: SidebarCloudRuntimeSources = {},
): {
  readonly cloudConnectedProjects: readonly SidebarProjectSnapshot[];
  readonly cloudInventoryProjects: readonly SidebarProjectSnapshot[];
  readonly localProjects: readonly SidebarProjectSnapshot[];
} {
  const cloudConnectedProjects: SidebarProjectSnapshot[] = [];
  const cloudInventoryProjects: SidebarProjectSnapshot[] = [];
  const localProjects: SidebarProjectSnapshot[] = [];
  const connectedProjectCountByRuntimeId = countConnectedProjectsByRuntimeId(
    projects,
    runtimeSources,
  );

  for (const project of projects) {
    const runtimeId = resolveCloudEnvironmentRuntimeId(project.environmentId, runtimeSources);
    // No runtime resolved: a desktop row, or a durable Environment neither the
    // saved registry nor the catalog names. The latter goes local for want of
    // anywhere better, and then renders nowhere: it has no Cloud row to sit
    // under, and `buildRuntimeProjectGroups` drops a cloud-classified id from
    // the device groups by shape. It reappears the moment either source names
    // its runtime, which is the state a connected Environment is in.
    if (runtimeId === null) {
      localProjects.push(project);
      continue;
    }
    if (project.source.actionAuthority === "inventory-only") {
      cloudInventoryProjects.push(project);
      continue;
    }
    const cloudKey = resolveConnectedCloudProjectKey({
      project,
      runtimeId,
      connectedProjectCount: connectedProjectCountByRuntimeId.get(runtimeId) ?? 0,
      projectIdentityByRuntimeId,
    });
    cloudConnectedProjects.push(
      project.projectKey === cloudKey ? project : { ...project, projectKey: cloudKey },
    );
  }

  return { cloudConnectedProjects, cloudInventoryProjects, localProjects };
}

/**
 * The connected cloud projects each runtime hosts.
 *
 * Every one of them, not the first. A runtime is per-owner and hosts every one
 * of that user's cloud projects, so keeping one dropped the rest from the
 * sidebar entirely while the `/new` picker still offered them (FOR-36359).
 *
 * Two store rows describing the SAME project still collapse, which is what the
 * map was built for: one project registered across environments, or surviving a
 * server restart, must not render twice.
 */
export function mapCloudConnectedProjectsByRuntimeId(
  projects: readonly SidebarProjectSnapshot[],
  runtimeSources: SidebarCloudRuntimeSources = {},
): ReadonlyMap<string, readonly SidebarProjectSnapshot[]> {
  const byRuntimeId = new Map<string, SidebarProjectSnapshot[]>();
  const seenProjectKeys = new Map<string, Set<string>>();
  for (const project of projects) {
    const runtimeId = resolveCloudEnvironmentRuntimeId(project.environmentId, runtimeSources);
    if (runtimeId === null) continue;
    const seen = seenProjectKeys.get(runtimeId) ?? new Set<string>();
    if (seen.has(project.projectKey)) continue;
    seen.add(project.projectKey);
    seenProjectKeys.set(runtimeId, seen);
    byRuntimeId.set(runtimeId, [...(byRuntimeId.get(runtimeId) ?? []), project]);
  }
  return byRuntimeId;
}

export function mapCloudInventoryProjectRefsByRuntimeId(
  projects: readonly SidebarProjectSnapshot[],
  runtimeSources: SidebarCloudRuntimeSources = {},
): ReadonlyMap<string, readonly ScopedProjectRef[]> {
  const refsByRuntimeId = new Map<string, ScopedProjectRef[]>();
  const seenByRuntimeId = new Map<string, Set<string>>();
  for (const project of projects) {
    const runtimeId = resolveCloudEnvironmentRuntimeId(project.environmentId, runtimeSources);
    if (runtimeId === null || project.source.actionAuthority !== "inventory-only") continue;
    const refs = refsByRuntimeId.get(runtimeId) ?? [];
    const seen = seenByRuntimeId.get(runtimeId) ?? new Set<string>();
    for (const ref of project.memberProjectRefs) {
      const key = `${ref.environmentId}:${ref.projectId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      refs.push(ref);
    }
    refsByRuntimeId.set(runtimeId, refs);
    seenByRuntimeId.set(runtimeId, seen);
  }
  return refsByRuntimeId;
}

export interface BuildVisibleConnectedCloudProjectsInput {
  readonly connectedProjectByRuntimeId: ReadonlyMap<string, readonly SidebarProjectSnapshot[]>;
  readonly additionalRefsByRuntimeId: ReadonlyMap<string, readonly ScopedProjectRef[]>;
  readonly catalogRows: readonly CloudProjectCatalogRow[];
  readonly catalogMembershipIsAuthoritative: boolean;
  readonly projectIsSuppressed: (project: SidebarProjectSnapshot) => boolean;
}

function catalogRowMatchesConnectedProjectIdentity(
  row: CloudProjectCatalogRow,
  project: SidebarProjectSnapshot,
): boolean {
  return project.projectKey === deriveCloudProjectKey(row);
}

/**
 * The catalog row behind a connected cloud project.
 *
 * Identity first, runtime second. Matching on the runtime alone handed every
 * project on a per-owner runtime the same row, so a removed project borrowed a
 * sibling's registration and stayed on screen (FOR-36359).
 *
 * `findRegisteringCatalogRow` is the matcher the `/new` picker uses, so the two
 * cannot answer this differently. The cloud-key bridge stays ahead of it for
 * the pre-connect swap, where the project carries the row's key and nothing
 * else yet names it.
 */
function cloudCatalogRowForConnectedProject(
  project: SidebarProjectSnapshot,
  catalogRows: readonly CloudProjectCatalogRow[],
): CloudProjectCatalogRow | undefined {
  return (
    catalogRows.find((row) => catalogRowMatchesConnectedProjectIdentity(row, project)) ??
    findRegisteringCatalogRow(
      catalogRows.map((row) => ({ row, ...toRegisteringCatalogRow(row) })),
      {
        environmentId: project.environmentId,
        projectId: project.id,
        cwd: project.cwd,
      },
    )?.row
  );
}

export function cloudCatalogRowHasConnectedProject(
  row: CloudProjectCatalogRow,
  catalogRows: readonly CloudProjectCatalogRow[],
  connectedProjectByRuntimeId: ReadonlyMap<string, readonly SidebarProjectSnapshot[]>,
): boolean {
  for (const projects of connectedProjectByRuntimeId.values()) {
    for (const project of projects) {
      if (cloudCatalogRowForConnectedProject(project, catalogRows) === row) return true;
    }
  }
  return false;
}

export function cloudCatalogMembershipIsAuthoritative(
  snapshot: Pick<
    CmmdForgeRuntimeListSnapshot,
    "connected" | "isLoading" | "stale" | "error" | "startupPhase"
  >,
): boolean {
  return (
    snapshot.connected &&
    !snapshot.isLoading &&
    !snapshot.stale &&
    snapshot.error === null &&
    snapshot.startupPhase !== "auth-refreshing"
  );
}

/**
 * Resolves connected cloud rows against the catalog membership boundary.
 *
 * A successful catalog response is authoritative: connected runtime inventory
 * cannot recreate a project after its registration has been removed. When the
 * catalog is unavailable, connected runtime rows remain as an offline fallback.
 */
/**
 * One connected project resolved against the catalog, or null when the catalog
 * proves its registration is gone.
 *
 * Split out so the caller is only the walk over runtimes and their projects.
 */
/**
 * The catalog row that names this project, never a fallback.
 *
 * What a project may WEAR, as opposed to what proves it still registered. See
 * `findNamingCatalogRow`.
 */
function cloudCatalogRowNamingConnectedProject(
  project: SidebarProjectSnapshot,
  catalogRows: readonly CloudProjectCatalogRow[],
  runtimeHostsOneProject: boolean,
): CloudProjectCatalogRow | undefined {
  // A runtime hosting one connected project has no ambiguity to resolve: its
  // row is that project's, which is the rule every other reader here applies.
  // Only a runtime hosting several has to be told which is which.
  if (runtimeHostsOneProject) return cloudCatalogRowForConnectedProject(project, catalogRows);
  const byCloudKey = catalogRows.find((row) =>
    catalogRowMatchesConnectedProjectIdentity(row, project),
  );
  if (byCloudKey) return byCloudKey;
  return findNamingCatalogRow(
    catalogRows.map((row) => ({
      row,
      environmentId: resolveCloudProjectEnvironmentId(row),
      workspaceRoot: row.workspaceRoot,
      resolvedRef: row.resolvedRef,
    })),
    { environmentId: project.environmentId, projectId: project.id, cwd: project.cwd },
  )?.row;
}

function resolveVisibleConnectedCloudProject(input: {
  readonly connectedProject: SidebarProjectSnapshot;
  readonly additionalRefs: readonly ScopedProjectRef[];
  readonly catalogRows: readonly CloudProjectCatalogRow[];
  readonly catalogMembershipIsAuthoritative: boolean;
  readonly projectIsSuppressed: (project: SidebarProjectSnapshot) => boolean;
  readonly runtimeHostsOneProject: boolean;
}): SidebarProjectSnapshot | null {
  const registeredCatalogRow = cloudCatalogRowForConnectedProject(
    input.connectedProject,
    input.catalogRows,
  );
  // One definition of this rule, shared with the `/new` picker. See
  // components/projects/pickableProject.ts.
  const registrationLive = isCloudRegistrationLive({
    catalogAuthoritative: input.catalogMembershipIsAuthoritative,
    provenUnregistered: registeredCatalogRow === undefined,
  });
  if (!registrationLive || input.projectIsSuppressed(input.connectedProject)) return null;

  const mergedProject = mergeSidebarProjectMemberRefs(input.connectedProject, input.additionalRefs);
  // Attribution comes from the row that NAMED this project, never from the one
  // that merely kept it visible. Removal's `locate()` treats a registration id
  // as deciding on its own, so a project wearing a sibling's id would
  // unregister the wrong project (FOR-36359).
  const namingRow = cloudCatalogRowNamingConnectedProject(
    input.connectedProject,
    input.catalogRows,
    input.runtimeHostsOneProject,
  );
  if (!namingRow) return mergedProject;
  return {
    ...mergedProject,
    cloudRemoval: namingRow.removal,
    ...(input.catalogMembershipIsAuthoritative
      ? { cloudRegistrationProjectId: namingRow.cmmdProjectId }
      : {}),
    canonicalIconUrl: namingRow.iconUrl,
    canonicalIcon: namingRow.icon,
  };
}

export function buildVisibleConnectedCloudProjects({
  connectedProjectByRuntimeId,
  additionalRefsByRuntimeId,
  catalogRows,
  catalogMembershipIsAuthoritative,
  projectIsSuppressed,
}: BuildVisibleConnectedCloudProjectsInput): readonly SidebarProjectSnapshot[] {
  return [...connectedProjectByRuntimeId].flatMap(([runtimeId, connectedProjects]) =>
    connectedProjects.flatMap((connectedProject) => {
      const visible = resolveVisibleConnectedCloudProject({
        connectedProject,
        additionalRefs: additionalRefsByRuntimeId.get(runtimeId) ?? [],
        catalogRows,
        catalogMembershipIsAuthoritative,
        projectIsSuppressed,
        runtimeHostsOneProject: connectedProjects.length === 1,
      });
      return visible ? [visible] : [];
    }),
  );
}
